import D from 'date-fp'
import * as R from 'ramda'
import { formatPrice } from 'sidecar-utility-library'

import { getSortedMembers } from 'utils/members'

/**
 * Generates a success message for changes to coverage. Uses replacedBy and
 * proratedAmount to determine which language to use.
 *
 * @param {object} coverage A coverage model to use for generated the message
 */
const notExist = R.isNil
const exist = R.complement(R.isNil)
const isPositive = amount => amount > 0
const isNegative = amount => amount < 0
export const getResultMessage = R.cond([
  [
    R.where({
      replacedBy: notExist,
      proratedAmount: isPositive,
    }),
    ({ proratedAmount }) =>
      `Your changes have been saved. We have also charged you ${formatPrice(
        Math.abs(proratedAmount),
      )} for the rest of the current billing cycle.`,
  ],
  [
    R.where({
      replacedBy: exist,
      proratedAmount: isPositive,
    }),
    () =>
      `Your changes are now being processed. Once done, we will charge you the difference for the rest of the current billing cycle.`,
  ],
  [
    R.where({
      replacedBy: notExist,
      proratedAmount: isNegative,
    }),
    ({ proratedAmount }) =>
      `Your changes have been saved. We have also refunded you ${formatPrice(
        Math.abs(proratedAmount),
      )} for the rest of the current billing cycle.`,
  ],
  [
    R.where({
      replacedBy: exist,
      proratedAmount: isNegative,
    }),
    () =>
      `Your changes are now being processed. Once done, we will refund you the difference for the rest of the current billing cycle.`,
  ],
  [R.T, () => 'Your changes have been saved.'],
])

/** Same as above but for members in paying groups. */
export const getGroupResultMessage = R.cond([
  [
    R.where({
      replacedBy: notExist,
    }),
    ({ paidUntilDate }) =>
      `Your changes have been saved. They will take into effect on your next billing period${
        paidUntilDate
          ? `, starting ${D.format('MMMM D, YYYY', new Date(paidUntilDate))}.`
          : '.'
      }`,
  ],
  [
    R.where({
      replacedBy: exist,
    }),
    ({ paidUntilDate }) =>
      `Your changes are now being processed. Once done, your changes will take into effect on your next billing period${
        paidUntilDate
          ? `, starting ${D.format('MMMM D, YYYY', new Date(paidUntilDate))}.`
          : '.'
      }`,
  ],
  [
    R.T,
    ({ paidUntilDate }) =>
      `Your changes have been saved. They will take into effect on your next billing period${
        paidUntilDate
          ? `, starting ${D.format('MMMM D, YYYY', new Date(paidUntilDate))}.`
          : '.'
      }`,
  ],
])

/**
 * Checks if a date is at least 2 weeks away. Including timezone in new Date
 * calculation to make it more resilient for remote testing.
 *
 * @param {string} date The date to check
 */
export const isDateTooSoon = date => {
  const daysToWait = 13
  const midnight = new Date()
  midnight.setHours(0)
  midnight.setMinutes(0)

  return D.diff('days', midnight, new Date(date)) < daysToWait
}

/**
 * Ensures that the UI designated startDate has the same timezone as the
 * coverage startDate provided by backend
 *
 * @param {string} chosenStartDate The date chosen by the user
 * @param {string} coverageStartDate The start date in the current coverage
 */
export const standardizeStartDateTimezone = (
  chosenStartDate,
  coverageStartDate,
) => {
  if (chosenStartDate && coverageStartDate) {
    //$1 = 2019-08-07T , $2 = 07 (this is the timezone) ,  $3 is the final hours
    const timezoneRegex = /(\d{4}-\d{2}-\d{2}T)(\d{2})(\S+$)/gm
    const coverageTimezone = coverageStartDate.replace(timezoneRegex, '$2')
    return chosenStartDate.replace(timezoneRegex, `$1${coverageTimezone}$3`)
  } else {
    return chosenStartDate
  }
}

/**
 * Returns the greater of two dates: one is the start date in the coverage the
 * other is a minimum date of 15 days from now.
 *
 * @param {object} applicant An applicant model to get the start date from
 */
export const calculateStartDate = (applicant = {}) => {
  // if the applicant provided has a startDate,
  // convert it to a date object so we can process.
  const startDate = !!applicant.startDate && new Date(applicant.startDate)

  // by default the minium date is 14 days from now but due to UTC
  // we set the minimum to be 15 days.
  let minDate = new Date()
  minDate.setDate(minDate.getDate() + 14)

  const calcDate = startDate ? R.max(startDate, minDate) : minDate
  calcDate.setHours(0)
  calcDate.setMinutes(0)
  calcDate.setSeconds(0)
  calcDate.setMilliseconds(0)

  return calcDate
}

/**
 * Returns the end date for the calendar modal. This prevents members for
 * selecting a start date that is more than 30 days in the future
 */
export const calculateEndDate = () => {
  // by default the max date is 30 days from now
  let maxDate = new Date()

  maxDate.setDate(maxDate.getDate() + 30)
  maxDate.setHours(0)
  maxDate.setMinutes(0)
  maxDate.setSeconds(0)
  maxDate.setMilliseconds(0)

  return maxDate
}

/**
 * Processes the getQuote checkout form into a coverage model that we can send
 * to the backend to generate PDFs and things.
 *
 * @param {object} formValues Object of form values
 * @param {array} members A list of members in the coverage
 * @param {object} coverage A coverage model
 */
export const processSubmissionForm = (formValues, members, coverage) => {
  const updatedMembers = addFormInfoToMembers(formValues, members)
  const street = R.path(['street'], formValues)
  const street2 = R.path(['street2'], formValues)
  const city = R.path(['city'], formValues)
  const phoneNumber = R.path(['phoneNumber'], formValues)

  return {
    members: updatedMembers,
    phoneNumber: phoneNumber ? phoneNumber.trim() : phoneNumber,
    address: {
      street: street ? street.trim() : street,
      // returning street2 just in case it's undefined
      // and needs to be that way.
      street2: street2 ? street2.trim() : street2,
      city: city ? city.trim() : city,
      state: R.path(['address', 'state'], coverage),
      zip: R.path(['address', 'zip'], coverage),
      lat: R.path(['address', 'lat'], coverage),
      lon: R.path(['address', 'lon'], coverage),
    },
    primaryCovered: coverage.primaryCovered,
    rateQuoteId: coverage.rateQuoteId,
    threeYearRateLock: coverage.threeYearRateLock,
    selectedPaymentOption: coverage.selectedPaymentOption,
    // I think in most cases, startDate will be set via initialValues
    // from the coverage startDate, but just in case it doesn't get set,
    // fallback to the coverage startDate
    startDate: formValues.startDate
      ? new Date(formValues.startDate).toISOString()
      : new Date(coverage.startDate).toISOString(),
  }
}

/**
 * Combines the form data we have with the member data we have so that we can
 * submit all of it to the backend.
 *
 * @param {obj} formValues The full checkout form data
 * @param {arr} members The list of members from Redux
 */
export const addFormInfoToMembers = (formValues, members) =>
  R.pipe(
    R.map(member => {
      const formMember =
        formValues.members.find(R.propEq('uuid', member.uuid)) || {}

      const memberInfo = {
        primary: member.primary || false,
        relationWithPrimary: member.relationWithPrimary,
        id: formMember.id,
        ssn: formMember.ssn,
        uuid: member.uuid,
        dateOfBirth: formMember.dateOfBirth,
        firstName: formMember.firstName,
        gender: formMember.gender,
        lastName: formMember.lastName,
        medicalHistory: formMember.medicalHistory,
        replaceCompany: formMember.replaceCompany,
        replaceCoverage: formMember.replaceCoverage,
        replaceInsurance: formMember.replaceInsurance,
        replacePolicy: formMember.replacePolicy,
        phoneNumber: formValues.phoneNumber,
        email: member.email,
      }

      // if replaceCoverage is false, then we want to strip
      // those values out from the member data.
      return R.when(
        mem => !mem.replaceInsurance,
        mem =>
          R.omit(['replaceCoverage', 'replaceCompany', 'replacePolicy'], mem),
      )(memberInfo)
    }),
    getSortedMembers,
  )(members)

/**
 * This ensures that the coverage coming in has a selectedPaymentMethod, which
 * may not be the case for packages estimated after package select
 *
 * @param {object} coverage
 * @param {string} paymentSchedule
 */
export const paymentScheduleMap = {
  QUARTERLY: 'quarterly',
  SEMI_ANNUAL: 'semiAnnual',
  ANNUAL: 'annual',
  MONTHLY: 'monthly',
}
export const addSelectedPaymentOption = (coverage, paymentSchedule) => {
  return R.either(R.isNil, R.isEmpty)(coverage.selectedPaymentOption)
    ? R.pipe(
        R.assoc(
          'selectedPaymentOption',
          R.path(
            ['paymentOption', paymentScheduleMap[paymentSchedule]],
            coverage,
          ),
        ),
        R.assoc('paymentSchedule', paymentSchedule),
        R.dissoc('paymentOption'),
      )(coverage)
    : coverage
}

const checkIfUndefinedAndReplace = prop =>
  R.ifElse(R.isNil, R.always(''), R.identity)(prop)

/**
 * We'll be now generating incomplete policy docs, this will ensure that we send
 * to the policy doc api a full address object, even if at the time of
 * generating, the member hasn't filled out all values
 *
 * @param {object} address Incomplete address object
 */
export const fillMissingAddressValues = address =>
  R.evolve(
    {
      street: x => checkIfUndefinedAndReplace(x),
      street2: x => checkIfUndefinedAndReplace(x),
      city: x => checkIfUndefinedAndReplace(x),
      state: x => checkIfUndefinedAndReplace(x),
      zip: x => checkIfUndefinedAndReplace(x),
    },
    address,
  )

/**
 * Helps to determine which states will go to the uncovered applicant page and
 * which states can pass on through to the regular flow
 *
 * @param {array} coveredStates List of covered states from initialSetup
 * @param {string} state Returned from lob api zip lookup
 * @returns {boolean}
 */
export const isBlockedState = (coveredStates = [], state = 'TX') =>
  !coveredStates.includes(state)

/**
 * There are many places where we need to turn the 2 letter state abbreviation
 * into the full state name and to show the insurance carrier for that state.
 * This is a map to let us do that easily.
 */

const USFIC = 'United States Fire Insurance Company'
const SAIC = 'SiriusPoint America Insurance Company'
export const USStates = {
  AL: { carrier: USFIC, fullName: 'Alabama' },
  AK: { carrier: '', fullName: 'Alaska' },
  AZ: { carrier: USFIC, fullName: 'Arizona' },
  AR: { carrier: USFIC, fullName: 'Arkansas' },
  CA: { carrier: '', fullName: 'California' },
  CO: { carrier: '', fullName: 'Colorado' },
  CT: { carrier: '', fullName: 'Connecticut' },
  DE: { carrier: '', fullName: 'Delaware' },
  FL: { carrier: USFIC, fullName: 'Florida' },
  GA: { carrier: USFIC, fullName: 'Georgia' },
  HI: { carrier: '', fullName: 'Hawaii' },
  ID: { carrier: '', fullName: 'Idaho' },
  IL: { carrier: USFIC, fullName: 'Illinois' },
  IN: { carrier: USFIC, fullName: 'Indiana' },
  IA: { carrier: '', fullName: 'Iowa' },
  KS: { carrier: '', fullName: 'Kansas' },
  KY: { carrier: SAIC, fullName: 'Kentucky' },
  LA: { carrier: SAIC, fullName: 'Louisiana' },
  ME: { carrier: '', fullName: 'Maine' },
  MD: { carrier: USFIC, fullName: 'Maryland' },
  MA: { carrier: '', fullName: 'Massachusetts' },
  MI: { carrier: USFIC, fullName: 'Michigan' },
  MN: { carrier: '', fullName: 'Minnesota' },
  MS: { carrier: SAIC, fullName: 'Mississippi' },
  MO: { carrier: '', fullName: 'Missouri' },
  MT: { carrier: '', fullName: 'Montana' },
  NE: { carrier: '', fullName: 'Nebraska' },
  NV: { carrier: '', fullName: 'Nevada' },
  NH: { carrier: '', fullName: 'New Hampshire' },
  NJ: { carrier: '', fullName: 'New Jersey' },
  NM: { carrier: '', fullName: 'New Mexico' },
  NY: { carrier: '', fullName: 'New York' },
  NC: { carrier: USFIC, fullName: 'North Carolina' },
  ND: { carrier: '', fullName: 'North Dakota' },
  OH: { carrier: SAIC, fullName: 'Ohio' },
  OK: { carrier: USFIC, fullName: 'Oklahoma' },
  OR: { carrier: '', fullName: 'Oregon' },
  PA: { carrier: '', fullName: 'Pennsylvania' },
  RI: { carrier: '', fullName: 'Rhode Island' },
  SC: { carrier: USFIC, fullName: 'South Carolina' },
  SD: { carrier: '', fullName: 'South Dakota' },
  TN: { carrier: USFIC, fullName: 'Tennessee' },
  TX: { carrier: USFIC, fullName: 'Texas' },
  UT: { carrier: SAIC, fullName: 'Utah' },
  VT: { carrier: '', fullName: 'Vermont' },
  VA: { carrier: '', fullName: 'Virginia' },
  WA: { carrier: '', fullName: 'Washington' },
  WV: { carrier: '', fullName: 'West Virginia' },
  WI: { carrier: '', fullName: 'Wisconsin' },
  WY: { carrier: '', fullName: 'Wyoming' },
}

export const plansByState = `Plans in Alabama, Arizona, Arkansas, Florida, Georgia, Illinois, 
  Indiana, Maryland, Michigan, North Carolina, Oklahoma, South Carolina, Tennessee and Texas are 
  underwritten by United States Fire Insurance Company. Plans in Kentucky, 
  Mississippi, Ohio and Utah are underwritten by SiriusPoint America Insurance Company.`

/**
 * Used to determine what legal text to render
 *
 * @param {Object} USStates Mapping of USStates to underwriter
 * @param {String} state Signup state
 * @param {bool} packages Is this the packages page
 */
export const showLegalRequirements = (state, packages, USStates = {}) => {
  const selectedState = R.propOr({}, state, USStates)

  if (selectedState.carrier) {
    return `Plans in ${selectedState.fullName} are underwritten by ${selectedState.carrier}`
  } else if (!selectedState.carrier && !packages) {
    return `Plans are currently not covered in ${selectedState.fullName}`
  } else {
    return plansByState
  }
}

export const USStatesOptions = [
  { value: 'AL', label: 'AL' },
  { value: 'AK', label: 'AK' },
  { value: 'AZ', label: 'AZ' },
  { value: 'AR', label: 'AR' },
  { value: 'CA', label: 'CA' },
  { value: 'CO', label: 'CO' },
  { value: 'CT', label: 'CT' },
  { value: 'DE', label: 'DE' },
  { value: 'FL', label: 'FL' },
  { value: 'GA', label: 'GA' },
  { value: 'HI', label: 'HI' },
  { value: 'ID', label: 'ID' },
  { value: 'IL', label: 'IL' },
  { value: 'IN', label: 'IN' },
  { value: 'IA', label: 'IA' },
  { value: 'KS', label: 'KS' },
  { value: 'KY', label: 'KY' },
  { value: 'LA', label: 'LA' },
  { value: 'ME', label: 'ME' },
  { value: 'MD', label: 'MD' },
  { value: 'MA', label: 'MA' },
  { value: 'MI', label: 'MI' },
  { value: 'MN', label: 'MN' },
  { value: 'MS', label: 'MS' },
  { value: 'MO', label: 'MO' },
  { value: 'MT', label: 'MT' },
  { value: 'NE', label: 'NE' },
  { value: 'NV', label: 'NV' },
  { value: 'NH', label: 'NH' },
  { value: 'NJ', label: 'NJ' },
  { value: 'NM', label: 'NM' },
  { value: 'NY', label: 'NY' },
  { value: 'NC', label: 'NC' },
  { value: 'ND', label: 'ND' },
  { value: 'OH', label: 'OH' },
  { value: 'OK', label: 'OK' },
  { value: 'OR', label: 'OR' },
  { value: 'PA', label: 'PA' },
  { value: 'RI', label: 'RI' },
  { value: 'SC', label: 'SC' },
  { value: 'SD', label: 'SD' },
  { value: 'TN', label: 'TN' },
  { value: 'TX', label: 'TX' },
  { value: 'UT', label: 'UT' },
  { value: 'VT', label: 'VT' },
  { value: 'VA', label: 'VA' },
  { value: 'WA', label: 'WA' },
  { value: 'WV', label: 'WV' },
  { value: 'WI', label: 'WI' },
  { value: 'WY', label: 'WY' },
]

/**
 * Pulls out all the states of each applicant, and then queues up the fetches so
 * it can do them all at once with Promise.all.
 *
 * @param {Array} applicants Applicants to get states from
 * @param {Function} fetchQuestions Action to dispatch to fetch health questions
 * @param {String} subproduct Value to indicate off exchange questions
 */
export const fetchAllStateQuestions = async (
  applicants,
  fetchQuestions,
  subproduct = '',
) => {
  // pull out all the unique states that our applicants
  // are trying to be covered in.
  const stateCodes = R.uniq(
    R.map(R.path(['mailingAddress', 'state']), applicants),
  )

  // create promises for fetching questions from each state
  const fetches = []
  for (let a in stateCodes) {
    if (Object.keys(USStates).includes(stateCodes[a])) {
      fetches.push(fetchQuestions(stateCodes[a], subproduct))
    }
  }

  // if this fails, the healthQuestions.error will be populated.
  // the success for these fetches will not be called because if
  // one comes back, it'll populate stateQuestions
  return Promise.all(fetches)
}

export const convertUuidToDate = applicant =>
  new Date(
    R.pipe(R.prop('uuid'), R.split(':'), R.dropLast(1), R.join(':'))(applicant),
  )

/**
 * Used to check in case an applicant tries to copy/paste more than 1000
 * characters after passing the initial reduxForm validation check of max 1000
 * characters. since our reduxForm validations for inputs run after the user
 * types/onBlur if an applicant initialy inputs less than 1000 characters to
 * pass the check then removes it and pastes more than 1000 characters the
 * button is still green since they haven't typed or blured off the input field.
 * So this will catch that and error them out if they try to be sneaky like
 * that
 *
 * @param {string} characters Procedures or voluntary exclusion
 * @returns {bool}
 */
export const lengthTooLong = (characters = '') => characters.length > 1000

/**
 * Used to transform the applicants and have the prescriptions and
 * relatedConditions from the prescriptions in a more formatted shape [{
 * ...applicant, prescriptions: [{name: string}], relatedConditions: [{uuid:
 * string, text: string}] }]
 *
 * @param {array} applicants List of applicants
 * @returns {array} FormattedApplicants
 */

export const extractApplicantRelatedConditions = R.pipe(
  // The applicants come in as an ordered map, so convert that to a [key, value] list
  (applicants = {}) => Object.entries(applicants),
  // convert the [key,value] list of applicants into object including
  //the uuid that was originally the key
  R.map(([uuid, applicant = {}]) => ({
    ...applicant,
    uuid,
  })),
  // for each applicant, keep only the prescriptions that have relatedConditions
  R.map(({ prescriptions = [], ...applicant }) => {
    const prescriptionsWithRelatedConditions = prescriptions
      .map(({ value: { name, relatedConditions = [] } }) => ({
        name,
        relatedConditions,
      }))
      .filter(({ relatedConditions = [] }) => !R.isEmpty(relatedConditions))

    return {
      ...applicant,
      prescriptions: prescriptionsWithRelatedConditions,
    }
  }),

  // Extract unique prescriptions and relatedConditions
  R.map(({ prescriptions = [], ...applicant }) => {
    const uniquePrescriptions = {}
    const uniqueRelatedConditions = {}

    prescriptions.forEach((prescription = {}) => {
      const { relatedConditions = [] } = prescription

      uniquePrescriptions[prescription.name] = prescription
      relatedConditions.forEach(condition => {
        uniqueRelatedConditions[condition.uuid] = condition
      })
    })
    return {
      ...applicant,
      prescriptions: Object.values(uniquePrescriptions),
      relatedConditions: Object.values(uniqueRelatedConditions),
    }
  }),
  // Only keep the applicants that still have prescriptions, which all now have relatedConditions
  R.filter(({ prescriptions = [] }) => !R.isEmpty(prescriptions)),
)

export const filterApplicantsWithKnockouts = (applicants = []) => {
  return applicants.filter((applicant = {}) => {
    const { relatedConditions = [] } = applicant

    const relatedConditionSelected = relatedConditions
      .map(({ uuid }) => applicant[uuid])
      .reduce((res, each) => res || each, false)

    // if a relatedCondition is not selected, continue showing the applicant
    return !relatedConditionSelected
  })
}
